import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useRef,
  useContext,
  useEffect,
} from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  createStyles,
} from "@material-ui/core";

import {
  CpfInput,
  CnpjInput,
  DialogoConfirmacao,
  DialogoCliente,
  AutoCompleteRemoto,
} from "../../../../../components";
import {
  ATALHO_VOLTAR,
  ATALHO_AVANCAR,
  SECONDARY_COLOR,
  SECONDARY_COLOR_OFFLINE,
  validarCNPJ,
  validarCPF,
  formatCPF,
  formatCNPJ,
  PRIMARY_COLOR,
  STATUS_ATIVO,
} from "../../../../../utils";
import { useSnackbar } from "notistack";
import { getListAllAPI } from "../../../../../services";

import { AppContext } from "../../../../../app/context";

const CANCELAR_VENDA_ACTION = ATALHO_VOLTAR;
const FECHAR_VENDA_ACTION = ATALHO_AVANCAR;
const ESC_ACTION = "Escape";
const CONSULTOR_PRECOS_ACTION = "F3";
const NOVO_CLIENTE_ACTION = "F9";
const EDITAR_CLIENTE_ACTION = "F7";

const useStyles = makeStyles((theme) =>
  createStyles({
    action: {
      display: "flex",
      justifyContent: "space-between",
    },
    root: {
      backgroundColor: theme.palette.background.paper,
    },
    button: {
      color: "white",
      backgroundColor: theme.palette.secondary.main,
    },
    disabledButton: {
      backgroundColor: "red",
    },
  })
);

const TELA_TIPO_NOTA = 1;
const TELA_INCLUIR_DOCUMENTO = 2;
const TELA_TIPO_DOCUMENTO = 3;
const TELA_CPF = 4;
const TELA_CNPJ = 5;
const TELA_INCLUIR_CADASTRO = 6;
const TELA_REVENDA = 7;

const CORES_TIPO_NOTA = {
  nfce: { corFundo: "#66bb6a", corFundoSelecionado: "#43a047" },
  nfe: { corFundo: "#fdd835", corFundoSelecionado: "#fbc02d" },
};

function TelaCartaoEscolha({ value, onChange, options, handleEnter, handleKey, cores }) {
  function alternar(direcao) {
    const total = options.length;
    const indiceAtual = options.findIndex((opcao) => opcao.id === value.id);
    const proximoIndice = (indiceAtual + direcao + total) % total;
    onChange(options[proximoIndice]);
  }

  // listener no window em vez de foco num input escondido: clicar fora
  // (fundo do diálogo, título, etc.) não pode fazer as setas pararem de funcionar
  useEffect(() => {
    function onKeyDown(e) {
      if (e.keyCode === 13) {
        handleEnter();
        return;
      }
      switch (e.key) {
        case "ArrowUp":
        case "ArrowLeft":
          alternar(-1);
          break;
        case "ArrowDown":
        case "ArrowRight":
          alternar(1);
          break;
        default:
          handleKey(e.keyCode, e.key);
          break;
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [value, options, handleEnter, handleKey]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        style={{ gap: "10px", width: "100%" }}
      >
        {options.map((opcao) => {
          const corSelecionada =
            (cores[opcao.id] && cores[opcao.id].corFundoSelecionado) || "#43a047";
          const selecionado = opcao.id === value.id;
          return (
            <Box
              key={opcao.id}
              onClick={() => onChange(opcao)}
              style={{
                cursor: "pointer",
                padding: "14px",
                borderRadius: "8px",
                backgroundColor: selecionado ? corSelecionada : "#eeeeee",
                border: selecionado
                  ? "3px solid #263238"
                  : "3px solid #e0e0e0",
                textAlign: "center",
                fontWeight: selecionado ? "bold" : 500,
                color: selecionado ? "#212121" : "#9e9e9e",
                boxSizing: "border-box",
              }}
            >
              {opcao.nome}
              {selecionado ? " ✓" : ""}
            </Box>
          );
        })}
      </Box>
      <p style={{ color: "#757575", fontSize: "12px", marginTop: "10px" }}>
        Use as setas (↑ ↓) para alternar
      </p>
    </div>
  );
}

function TelaCpf({ value, onChange, erro, handleEnter, handleKey }) {
  return (
    <div>
      <CpfInput
        name="cpf"
        handleEnter={() => {
          if (erro === "") {
            handleEnter();
          }
        }}
        label="CPF"
        handleKey={handleKey}
        value={value}
        onChange={(value) => onChange(value.target.value)}
        error={erro !== ""}
        helperText={erro}
        fullWidth
        autoCompleteState="new-password"
        autoFocus
      />
    </div>
  );
}

function TelaCnpj({ value, onChange, erro, handleEnter, handleKey }) {
  return (
    <div>
      <CnpjInput
        name="cnpj"
        handleEnter={() => {
          if (erro === "") {
            handleEnter();
          }
        }}
        label="CNPJ"
        handleKey={handleKey}
        value={value}
        onChange={(value) => onChange(value.target.value)}
        error={erro !== ""}
        helperText={erro}
        fullWidth
        autoCompleteState="new-password"
        autoFocus
      />
    </div>
  );
}

const useStylesTelaCliente = makeStyles((theme) => ({
  "@keyframes fadeIn": {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  appBar: {
    position: "relative",
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  textField: {},
  btnImprimir: {
    backgroundColor: theme.palette.secondary.main,
    opacity: 0.4,
  },
  header: {
    height: "17%",
  },
  body: {
    height: "83%",
  },
  footer: {
    height: "15vh",
    padding: "10px",
    paddingBottom: "20px",
  },
  textOverflow: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

function TelaCliente({ value, onChange, erro, handleEnter, handleKey, tipoNota }) {
  const refNome = useRef(null);
  const refNomeFantasia = useRef(null);
  const refCpf = useRef(null);
  const refCnpj = useRef(null);
  const classes = useStylesTelaCliente();

  const colorHeader = SECONDARY_COLOR;

  const incluirAnonimo = tipoNota !== TIPO_NOTA_NFE;
  const cadastroIncompletoSefaz = tipoNota === TIPO_NOTA_NFE && value && !value.is_cadastro_completo_sefaz;
  const clienteForaDoEstado =
    tipoNota === TIPO_NOTA_NFE && isClienteForaDoEstado(value);
  // cadastro marcado como "não precisa NFe" nunca passou pelo teste da SEFAZ —
  // não é um cadastro incompleto/errado, só nunca foi validado pra esse fim
  const clienteNuncaValidadoNfe = cadastroIncompletoSefaz && !value.precisa_nfe;

  return (
    <Box padding="10px" className={classes.header}>
      <Box
        display="flex"
        css={{
          backgroundColor: "white",
          opacity: "0.75",
          height: "100%",
          padding: "0px 10px",
          flexDirection: "column",
        }}
      >
        <Box display="flex" alignItems="center" flex={1}>
          <AutoCompleteRemoto
            ref={refNome}
            id="cliente"
            name="cliente"
            label="Responsável"
            value={value}
            onChange={(value) => {
              onChange(value);
            }}
            handleKey={handleKey}
            handleEnter={() => {
              if (refNomeFantasia.current) {
                refNomeFantasia.current.focus();
                refNomeFantasia.current.select();
              }
            }}
            resource="clientes"
            nested={["cidade.estado", "categoria"]}
            toString={false}
            fullwidth
            autoFocus
            filters={
              incluirAnonimo
                ? { status: STATUS_ATIVO }
                : { status: STATUS_ATIVO, ids_excluso: [1] }
            }
          />
          <AutoCompleteRemoto
            ref={refNomeFantasia}
            id="cliente"
            name="cliente"
            label="Nome Fantasia"
            value={value}
            onChange={(value) => {
              onChange(value);
            }}
            handleKey={handleKey}
            handleEnter={() => {
              if (refCpf.current) {
                refCpf.current.focus();
                refCpf.current.select();
              }
            }}
            resource="clientes"
            campo="nome_fantasia"
            nested={["cidade.estado", "categoria"]}
            toString={false}
            style={{ marginLeft: "10px" }}
            fullwidth
            filters={
              incluirAnonimo
                ? { status: STATUS_ATIVO }
                : { status: STATUS_ATIVO, ids_excluso: [1] }
            }
          />
        </Box>
        <Box display="flex" alignItems="center" flex={1}>
          <AutoCompleteRemoto
            ref={refCpf}
            id="cliente"
            name="cliente"
            label="CPF"
            value={value}
            onChange={(value) => {
              onChange(value);
            }}
            handleKey={handleKey}
            handleEnter={() => {
              if (refCnpj.current) {
                refCnpj.current.focus();
                refCnpj.current.select();
              }
            }}
            resource="clientes"
            campo="cpf"
            campoPesquisa="cpf"
            nested={["cidade.estado", "categoria"]}
            toString={false}
            fullwidth
            filters={
              incluirAnonimo
                ? { status: STATUS_ATIVO }
                : { status: STATUS_ATIVO, ids_excluso: [1] }
            }
          />
          <AutoCompleteRemoto
            ref={refCnpj}
            id="cliente"
            name="cliente"
            label="CNPJ"
            value={value}
            onChange={handleKey}
            handleKey={handleKey}
            handleEnter={() => {
              if (refNome.current) {
                refNome.current.focus();
                refNome.current.select();
              }
            }}
            resource="clientes"
            campo="cnpj"
            campoPesquisa="cnpj"
            nested={["cidade.estado", "categoria"]}
            toString={false}
            style={{ marginLeft: "10px" }}
            fullwidth
            filters={
              incluirAnonimo
                ? { status: STATUS_ATIVO }
                : { status: STATUS_ATIVO, ids_excluso: [1] }
            }
          />
        </Box>
        {!value && (
          <Box
            style={{
              backgroundColor: "#d1ecf1",
              border: "1px solid #17a2b8",
              borderRadius: "5px",
              padding: "10px",
              margin: "10px 0px 0px",
              color: "#0c5460",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            ℹ Nenhum cliente selecionado. Pesquise um cliente nos campos
            acima, ou clique em "Novo cliente (F9)" para cadastrar um novo.
          </Box>
        )}
        {cadastroIncompletoSefaz && (
          <Box
            style={{
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "5px",
              padding: "10px",
              margin: "10px 0px 10px",
              color: "#856404",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {clienteNuncaValidadoNfe
              ? '⚠ Este cliente nunca foi validado para emissão de NFe (o cadastro foi salvo como "não precisa NFe"). Clique em "Editar cliente (F7)" para validar, ou "Novo cliente (F9)" para cadastrar outro.'
              : '⚠ O cadastro deste cliente está incompleto perante a SEFAZ e não permite emissão de NFe. Clique em "Editar cliente (F7)" para completar o cadastro, ou "Novo cliente (F9)" para cadastrar outro.'}
          </Box>
        )}
        {clienteForaDoEstado && (
          <Box
            style={{
              backgroundColor: "#f8d7da",
              border: "1px solid #dc3545",
              borderRadius: "5px",
              padding: "10px",
              margin: "10px 0px 0px",
              color: "#721c24",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            ⛔ Este cliente é de {value.cidade.estado.sigla}. A emissão de NFe
            para fora de {UF_EMITENTE} ainda não é permitida — essa
            funcionalidade está em desenvolvimento. Selecione um cliente de{" "}
            {UF_EMITENTE}, ou volte e escolha NFCe.
          </Box>
        )}
        {value && (
          <Box
            display="flex"
            justifyContent="space-between"
            flex={1}
            style={{
              border: `1px solid ${PRIMARY_COLOR}`,
              marginLeft: "0px",
              padding: "5px",
              borderRadius: "5px",
              marginBottom: "10px",
            }}
          >
            <Box
              marginLeft="10px"
              display="flex"
              flexDirection="column"
              flex={1}
            >
              <Box
                marginLeft="0px"
                fontSize="15px"
                fontWeight="bold"
                display="flex"
                flex={1}
              >
                <p
                  className={classes.textOverflow}
                  style={{
                    marginBottom: "0px",
                    color: colorHeader,
                    marginRight: "10px",
                  }}
                >
                  Responsável:
                </p>
                {value.nome}
              </Box>
              <Box
                marginLeft="0px"
                fontSize="15px"
                fontWeight="bold"
                display="flex"
                flex={1}
              >
                <p
                  className={classes.textOverflow}
                  style={{
                    marginBottom: "0px",
                    color: colorHeader,
                    marginRight: "10px",
                  }}
                >
                  Razão Social:
                </p>
                {value.razao_social}
              </Box>
              <Box
                marginLeft="0px"
                fontSize="15px"
                fontWeight="bold"
                display="flex"
                flex={1}
              >
                <p
                  className={classes.textOverflow}
                  style={{
                    marginBottom: "0px",
                    color: colorHeader,
                    marginRight: "10px",
                  }}
                >
                  CPF:
                </p>
                {formatCPF(value.cpf)}
              </Box>
              <Box
                marginLeft="0px"
                fontSize="15px"
                fontWeight="bold"
                display="flex"
                flex={1}
              >
                <p
                  className={classes.textOverflow}
                  style={{
                    marginBottom: "0px",
                    color: colorHeader,
                    marginRight: "10px",
                  }}
                >
                  CNPJ:
                </p>
                {formatCNPJ(value.cnpj)}
              </Box>
            </Box>
            <Box
              marginLeft="10px"
              display="flex"
              flexDirection="column"
              flex={1}
            >
              <Box
                marginLeft="0px"
                fontSize="15px"
                fontWeight="bold"
                display="flex"
                flex={1}
                justifyContent="flex-start"
              >
                <p
                  className={classes.textOverflow}
                  style={{
                    marginBottom: "0px",
                    color: colorHeader,
                    marginRight: "10px",
                  }}
                >
                  Nome Fantasia:
                </p>
                {value.nome_fantasia}
              </Box>
              <Box
                marginLeft="0px"
                fontSize="15px"
                fontWeight="bold"
                display="flex"
                flex={1}
                justifyContent="flex-start"
              >
                <p
                  className={classes.textOverflow}
                  style={{
                    marginBottom: "0px",
                    color: colorHeader,
                    marginRight: "10px",
                  }}
                >
                  Bairro:
                </p>
                {value.bairro}
              </Box>
              <Box
                marginLeft="0px"
                fontSize="15px"
                fontWeight="bold"
                display="flex"
                flex={1}
                justifyContent="flex-start"
              >
                <p
                  className={classes.textOverflow}
                  style={{
                    marginBottom: "0px",
                    color: colorHeader,
                    marginRight: "10px",
                  }}
                >
                  Cidade:
                </p>
                {value.cidade
                  ? `${value.cidade.nome} - ${value.cidade.estado.sigla}`
                  : ""}
              </Box>
              <Box
                marginLeft="0px"
                fontSize="15px"
                fontWeight="bold"
                display="flex"
                flex={1}
                justifyContent="flex-start"
              >
                <p
                  className={classes.textOverflow}
                  style={{
                    marginBottom: "0px",
                    color: colorHeader,
                    marginRight: "10px",
                  }}
                >
                  Logradouro:
                </p>
                {value.logradouro}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

const TIPO_NOTA_NFE = "nfe";
const TIPO_NOTA_NFCE = "nfce";

const UF_EMITENTE = "PE";

function isClienteForaDoEstado(cliente) {
  const sigla =
    cliente && cliente.cidade && cliente.cidade.estado
      ? cliente.cidade.estado.sigla
      : null;
  // sem cidade cadastrada a UF é desconhecida: não bloqueia
  return !!sigla && sigla !== UF_EMITENTE;
}

const optionsTipoNota = [
  {
    id: TIPO_NOTA_NFCE,
    nome: "NFCe (cupomzinho)",
  },
  {
    id: TIPO_NOTA_NFE,
    nome: "NFe (nota grande)",
  },
];

const TIPO_DOCUMENTO_CPF = "cpf";
const TIPO_DOCUMENTO_CNPJ = "cnpj";

const optionsTipoDocumento = [
  {
    id: TIPO_DOCUMENTO_CPF,
    nome: "CPF",
  },
  {
    id: TIPO_DOCUMENTO_CNPJ,
    nome: "CNPJ",
  },
];

const CORES_TIPO_DOCUMENTO = {
  [TIPO_DOCUMENTO_CPF]: { corFundoSelecionado: "#43a047" },
  [TIPO_DOCUMENTO_CNPJ]: { corFundoSelecionado: "#43a047" },
};

const RESPOSTA_PERGUNTA_SIM = "sim";
const RESPOSTA_PERGUNTA_NAO = "nao";

const optionsRespostaPergunta = [
  {
    id: RESPOSTA_PERGUNTA_SIM,
    nome: "SIM",
  },
  {
    id: RESPOSTA_PERGUNTA_NAO,
    nome: "NÃO",
  },
];

const optionsRespostaPergunta2 = [
  {
    id: RESPOSTA_PERGUNTA_NAO,
    nome: "NÃO",
  },
  {
    id: RESPOSTA_PERGUNTA_SIM,
    nome: "SIM",
  },
];

const CORES_RESPOSTA_PERGUNTA = {
  [RESPOSTA_PERGUNTA_NAO]: { corFundoSelecionado: "#ef5350" },
  [RESPOSTA_PERGUNTA_SIM]: { corFundoSelecionado: "#66bb6a" },
};

const TIPO_CLIENTE_USO_CONSUMO = "uso_consumo";
const TIPO_CLIENTE_REVENDA = "revenda";

const IND_IE_DEST_NAO_CONTRIBUINTE = "9";

const optionsTipoCliente = [
  {
    id: TIPO_CLIENTE_USO_CONSUMO,
    nome: "USO E CONSUMO",
  },
  {
    id: TIPO_CLIENTE_REVENDA,
    nome: "REVENDA",
  },
];

const CORES_TIPO_CLIENTE = {
  [TIPO_CLIENTE_USO_CONSUMO]: { corFundoSelecionado: "#42a5f5" },
  [TIPO_CLIENTE_REVENDA]: { corFundoSelecionado: "#ff7043" },
};

const DialogoCpfNota = forwardRef(
  ({ handleProsseguir = () => {}, irParaTelaInit, clienteInicial, usar_biometria }, ref) => {
    const [open, setOpen] = useState(false);
    const [cpf, setCpf] = useState("");
    const [cnpj, setCnpj] = useState("");
    const [tipoDocumento, setTipoDocumento] = useState(null);
    const [incluirDocumento, setIncluirDocumento] = useState(null);
    const [tipoNota, setTipoNota] = useState(optionsTipoNota[0]);
    const [tipoCliente, setTipoCliente] = useState(optionsTipoCliente[0]);
    const [tela, setTela] = useState(TELA_TIPO_NOTA);
    const refDialogoConfirmacao = useRef(null);
    const classes = useStyles();
    const refDialogoCliente = useRef(null);
    const { enqueueSnackbar } = useSnackbar();
    const [cliente, setCliente] = useState(null);

    function handleClose() {
      setOpen(false);
    }

    function saudacaoPorHorario() {
      const agora = new Date();
      const hora = agora.getHours();

      if (hora >= 5 && hora < 12) {
        return "Bom dia!";
      } else if (hora >= 12 && hora < 18) {
        return "Boa tarde!";
      } else {
        return "Boa noite!";
      }
    }

    useImperativeHandle(ref, () => ({
      handleOpen(value) {
        setCpf("");
        setCnpj("");
        setOpen(true);
        setTela(TELA_TIPO_NOTA);
        setTipoNota(optionsTipoNota[0]);
        setTipoCliente(optionsTipoCliente[0]);
        setIncluirDocumento(optionsRespostaPergunta2[0]);
        setTipoDocumento(optionsTipoDocumento[0]);
        setCliente(clienteInicial);
      },
    }));

    function getDados() {
      let cpfFinal = null;
      let cnpjFinal = null;
      if (incluirDocumento.id === RESPOSTA_PERGUNTA_SIM) {
        if (tipoDocumento.id === TIPO_DOCUMENTO_CPF) {
          cpfFinal = cpf;
          cnpjFinal = null;
        } else {
          cpfFinal = null;
          cnpjFinal = cnpj;
        }
      } else {
        cpfFinal = null;
        cnpjFinal = null;
      }

      return {
        cpf: cpfFinal,
        cnpj: cnpjFinal,
        tipoNota: tipoNota.id,
        revenda:
          tipoNota.id === TIPO_NOTA_NFE &&
          tipoCliente.id === TIPO_CLIENTE_REVENDA,
      };
    }

    function handleSalvar(cliente) {
      setOpen(false);
      const dados = getDados();
      handleProsseguir({
        ...dados,
        cliente,
      });
    }

    function cancelar() {
      if (refDialogoConfirmacao.current) {
        refDialogoConfirmacao.current.handleOpen(
          "Deseja Cancelar esta Venda?",
          "Tem certeza de que deseja cancelar esta venda?",
          1
        );
      }
    }

    async function getCliente(filtro) {
      const data = await getListAllAPI("clientes", ["id", "asc"], filtro, [
        "cidade.estado",
        "categoria",
      ]);

      if (data.data.length > 0) {
        return data.data[0];
      } else {
        return null;
      }
    }

    async function verificaClienteExistente() {
      if (incluirDocumento.id === RESPOSTA_PERGUNTA_SIM) {
        if (tipoDocumento.id === TIPO_DOCUMENTO_CPF) {
          const cliente = await getCliente({ cpf });
          return cliente;
        } else {
          const cliente = await getCliente({ cnpj });
          return cliente;
        }
      } else {
        return null;
      }
    }

    function irParaTelaCliente() {
      if (tipoNota.id === TIPO_NOTA_NFE) setCliente(null);
      setTela(TELA_INCLUIR_CADASTRO);
    }

    async function verifica() {
      const cliente = await verificaClienteExistente();
      if (cliente) {
        if (tipoNota.id === TIPO_NOTA_NFE && !cliente.is_cadastro_completo_sefaz) {
          setCliente(cliente);
          setTela(TELA_INCLUIR_CADASTRO);
        } else {
          handleSalvar(cliente);
        }
      } else {
        irParaTelaCliente();
      }
    }

    function handleActions(action) {
      switch (action) {
        case FECHAR_VENDA_ACTION: {
          switch (tela) {
            case TELA_TIPO_NOTA:
              if (tipoNota.id === TIPO_NOTA_NFE) {
                irParaTelaCliente();
              } else {
                setTela(TELA_INCLUIR_DOCUMENTO);
              }
              break;
            case TELA_REVENDA:
              if (!erroTipoCliente && !erroFinalidadeRevenda) {
                handleSalvar(cliente);
              }
              break;
            case TELA_INCLUIR_DOCUMENTO:
              if (!erroIncluirDocumento) {
                if (incluirDocumento.id === RESPOSTA_PERGUNTA_SIM) {
                  setTela(TELA_TIPO_DOCUMENTO);
                } else {
                  irParaTelaCliente();
                }
              }
              break;
            case TELA_TIPO_DOCUMENTO:
              if (!erroTipoDocumento) {
                if (tipoDocumento.id === TIPO_DOCUMENTO_CPF) {
                  setTela(TELA_CPF);
                } else {
                  setTela(TELA_CNPJ);
                }
              }
              break;
            case TELA_CPF:
              if (!erroCpf) {
                verifica();
              }
              break;
            case TELA_CNPJ:
              if (!erroCnpj) {
                verifica();
              }
              break;
            case TELA_INCLUIR_CADASTRO:
              if (!erroCliente) {
                if (tipoNota.id === TIPO_NOTA_NFE) {
                  setTela(TELA_REVENDA);
                } else {
                  handleSalvar(cliente);
                }
              } else {
                enqueueSnackbar(erroCliente, {
                  variant: "warning",
                });
              }
              break;
            default:
              break;
          }
          break;
        }
        case CANCELAR_VENDA_ACTION: {
          switch (tela) {
            case TELA_TIPO_NOTA:
              cancelar();
              break;
            case TELA_REVENDA:
              setTela(TELA_INCLUIR_CADASTRO);
              break;
            case TELA_INCLUIR_DOCUMENTO:
              setTela(TELA_TIPO_NOTA);
              break;
            case TELA_TIPO_DOCUMENTO:
              setTela(TELA_INCLUIR_DOCUMENTO);
              break;
            case TELA_CPF:
              setTela(TELA_TIPO_DOCUMENTO);
              break;
            case TELA_CNPJ:
              setTela(TELA_TIPO_DOCUMENTO);
              break;
            case TELA_INCLUIR_CADASTRO:
              if (tipoNota.id === TIPO_NOTA_NFE) {
                setTela(TELA_TIPO_NOTA);
              } else if (incluirDocumento.id === RESPOSTA_PERGUNTA_SIM) {
                if (tipoDocumento.id === TIPO_DOCUMENTO_CPF) {
                  setTela(TELA_CPF);
                } else {
                  setTela(TELA_CNPJ);
                }
              } else {
                setTela(TELA_INCLUIR_DOCUMENTO);
              }

              break;
            default:
              break;
          }
          break;
        }
        case ESC_ACTION: {
          break;
        }
        case EDITAR_CLIENTE_ACTION:
          if (tela === TELA_INCLUIR_CADASTRO) {
            editarCliente();
          }
          break;
        case NOVO_CLIENTE_ACTION:
          if (tela === TELA_INCLUIR_CADASTRO) {
            createCliente();
          }
          break;
        default:
          break;
      }
    }

    function handleKey(keyCode, keyName) {
      handleActions(keyName);
    }

    function validateCnpj() {
      let error = "";
      if (cnpj) {
        if (cnpj.length < 14) error = "O CNPJ deve ter 14 dígitos";
        else {
          if (!validarCNPJ(cnpj)) error = "CNPJ Inválido";
        }
      } else {
        error = "Este campo é obrigatório";
      }
      return error;
    }

    const erroCnpj = validateCnpj();

    function validateCpf() {
      let error = "";
      if (cpf) {
        if (cpf.length < 11) error = "O CPF deve ter 11 dígitos";
        else {
          if (!validarCPF(cpf)) error = "CPF Inválido";
        }
      } else {
        error = "Este campo é obrigatório";
      }
      return error;
    }

    const erroCpf = validateCpf();

    function validateTipoNota() {
      let error = "";
      if (!tipoNota) {
        error = "Este campo é obrigatório";
      }
      return error;
    }

    const erroTipoNota = validateTipoNota();

    function validateTipoCliente() {
      let error = "";
      if (!tipoCliente) {
        error = "Este campo é obrigatório";
      }
      return error;
    }

    const erroTipoCliente = validateTipoCliente();

    function validateFinalidadeRevenda() {
      let error = "";
      if (
        tipoCliente.id === TIPO_CLIENTE_REVENDA &&
        cliente &&
        cliente.ind_ie_dest === IND_IE_DEST_NAO_CONTRIBUINTE
      ) {
        error =
          'Cliente não contribuinte não pode comprar para revenda (rejeição SEFAZ 696). Selecione "Uso e Consumo", ou edite o cadastro do cliente pra Contribuinte/Isento.';
      }
      return error;
    }

    const erroFinalidadeRevenda = validateFinalidadeRevenda();

    function validateTipoDocumento() {
      let error = "";
      if (!tipoDocumento) {
        error = "Este campo é obrigatório";
      }
      return error;
    }

    const erroTipoDocumento = validateTipoDocumento();

    function validateIncluirDocumento() {
      let error = "";
      if (!incluirDocumento) {
        error = "Este campo é obrigatório";
      }
      return error;
    }

    const erroIncluirDocumento = validateIncluirDocumento();

    function validateCliente() {
      let error = "";
      if (!cliente) {
        error = "Este campo é obrigatório";
      } else if (tipoNota.id === TIPO_NOTA_NFE && !cliente.is_cadastro_completo_sefaz) {
        error =
          "O cadastro deste cliente está incompleto perante a SEFAZ. Complete o cadastro para emitir NFe.";
      } else if (tipoNota.id === TIPO_NOTA_NFE && isClienteForaDoEstado(cliente)) {
        /*  */error =
          `A emissão de NFe para clientes de fora de ${UF_EMITENTE} ainda não é permitida. ` +
          "Essa funcionalidade está em desenvolvimento.";
        error = cliente.precisa_nfe
          ? "O cadastro deste cliente está incompleto perante a SEFAZ. Complete o cadastro para emitir NFe."
          : "Este cliente nunca foi validado para emissão de NFe. Edite o cadastro e valide antes de emitir a nota.";
      }
      return error;
    }

    const erroCliente = validateCliente();
    const avancarDesabilitadoTelaCliente =
      tela === TELA_INCLUIR_CADASTRO && !!erroCliente;

    let componente = null;

    switch (tela) {
      case TELA_TIPO_NOTA:
        componente = (
          <TelaCartaoEscolha
            value={tipoNota}
            onChange={(value) => setTipoNota(value)}
            options={optionsTipoNota}
            handleEnter={() => {
              handleActions(FECHAR_VENDA_ACTION);
            }}
            handleKey={handleKey}
            cores={CORES_TIPO_NOTA}
          />
        );
        break;
      case TELA_REVENDA:
        componente = (
          <>
            <TelaCartaoEscolha
              value={tipoCliente}
              onChange={(value) => setTipoCliente(value)}
              options={optionsTipoCliente}
              handleEnter={() => {
                handleActions(FECHAR_VENDA_ACTION);
              }}
              handleKey={handleKey}
              cores={CORES_TIPO_CLIENTE}
            />
            {!!erroFinalidadeRevenda && (
              <Box
                style={{
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "5px",
                  padding: "10px",
                  margin: "10px 0px 0px",
                  color: "#856404",
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                {`⚠ ${erroFinalidadeRevenda}`}
              </Box>
            )}
          </>
        );
        break;
      case TELA_INCLUIR_DOCUMENTO:
        componente = (
          <TelaCartaoEscolha
            value={incluirDocumento}
            onChange={(value) => setIncluirDocumento(value)}
            options={optionsRespostaPergunta2}
            handleEnter={() => {
              handleActions(FECHAR_VENDA_ACTION);
            }}
            handleKey={handleKey}
            cores={CORES_RESPOSTA_PERGUNTA}
          />
        );
        break;
      case TELA_TIPO_DOCUMENTO:
        componente = (
          <TelaCartaoEscolha
            value={tipoDocumento}
            onChange={(value) => setTipoDocumento(value)}
            options={optionsTipoDocumento}
            handleEnter={() => {
              handleActions(FECHAR_VENDA_ACTION);
            }}
            handleKey={handleKey}
            cores={CORES_TIPO_DOCUMENTO}
          />
        );
        break;
      case TELA_CPF:
        componente = (
          <TelaCpf
            value={cpf}
            onChange={(value) => setCpf(value)}
            erro={erroCpf}
            handleEnter={() => {
              handleActions(FECHAR_VENDA_ACTION);
            }}
            handleKey={handleKey}
          />
        );
        break;
      case TELA_CNPJ:
        componente = (
          <TelaCnpj
            value={cnpj}
            onChange={(value) => setCnpj(value)}
            erro={erroCnpj}
            handleEnter={() => {
              handleActions(FECHAR_VENDA_ACTION);
            }}
            handleKey={handleKey}
          />
        );
        break;
      case TELA_INCLUIR_CADASTRO:
        componente = (
          <TelaCliente
            value={cliente}
            onChange={(value) => setCliente(value)}
            erro={""}
            handleEnter={() => {
              handleActions(FECHAR_VENDA_ACTION);
            }}
            handleKey={handleKey}
            tipoNota={tipoNota.id}
          />
        );
        break;
      default:
        break;
    }

    function getEtapas() {
      const etapas = [TELA_TIPO_NOTA];
      if (tipoNota.id === TIPO_NOTA_NFE) {
        etapas.push(TELA_INCLUIR_CADASTRO, TELA_REVENDA);
      } else {
        etapas.push(TELA_INCLUIR_DOCUMENTO);
        if (incluirDocumento && incluirDocumento.id === RESPOSTA_PERGUNTA_SIM) {
          etapas.push(
            TELA_TIPO_DOCUMENTO,
            tipoDocumento && tipoDocumento.id === TIPO_DOCUMENTO_CNPJ
              ? TELA_CNPJ
              : TELA_CPF,
            TELA_INCLUIR_CADASTRO
          );
        } else {
          etapas.push(TELA_INCLUIR_CADASTRO);
        }
      }
      return etapas;
    }

    const etapas = getEtapas();
    let etapaAtual = etapas.indexOf(tela) + 1;
    let totalEtapas = etapas.length;
    if (etapaAtual === 0) {
      // tela atual não estava prevista na sequência estimada (ex: cliente não
      // encontrado pelo CPF/CNPJ abriu o cadastro de qualquer forma) — mostra
      // como a última etapa em vez de sumir com o indicador
      etapaAtual = totalEtapas + 1;
      totalEtapas = etapaAtual;
    }

    function getTitle() {
      switch (tela) {
        case TELA_TIPO_NOTA:
          return `${saudacaoPorHorario()}, tudo bem? Deseja Nfe (nota grande), ou somente NFCe (cupomzinho)?`;
        case TELA_REVENDA:
          return "Esta venda é para revenda ou para uso e consumo?";
        case TELA_INCLUIR_DOCUMENTO:
          return "Deseja declarar a Receita Federal CPF ou CNPJ?";
        case TELA_TIPO_DOCUMENTO:
          return "Qual o tipo de Documento";
        case TELA_CPF:
          return "Me diga o seu CPF";
        case TELA_CNPJ:
          return "Me diga o seu CNPJ";
        case TELA_INCLUIR_CADASTRO:
          return "Cadastro do Cliente";
        default:
          return "";
      }
    }

    async function updateCliente(idUpdate) {
      try {
        const data = await getListAllAPI(
          "clientes",
          ["id", "asc"],
          { id: [idUpdate] },
          ["cidade.estado", "categoria"]
        );
        if (data.data.length > 0) {
          setCliente(data.data[0]);
        } else {
          throw "Este cliente não existe!";
        }
      } catch (erros) {
        enqueueSnackbar(`INTERNET: ERRO AO OBTER O CLIENTE!`, {
          variant: "error",
        });
      }
    }

    function createCliente() {
      if (refDialogoCliente.current) refDialogoCliente.current.handleCreate();
    }

    function editarCliente() {
      if (refDialogoCliente.current) {
        const idCliente = cliente ? cliente.id : 0;
        if (idCliente > 1) {
          refDialogoCliente.current.handleEdit(idCliente);
        } else {
          enqueueSnackbar(`Este cliente não pode ser editado!`, {
            variant: "error",
          });
        }
      }
    }

    return (
      <div>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          disableBackdropClick
          disableEscapeKeyDown
          fullWidth
          maxWidth={tela === TELA_INCLUIR_CADASTRO ? "md" : "xs"}
        >
          <DialogTitle
            id="alert-dialog-title"
            style={{
              color: "white",
              backgroundColor: SECONDARY_COLOR,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: "bold",
                opacity: 0.85,
                marginBottom: "4px",
              }}
            >
              {`Etapa ${etapaAtual} de ${totalEtapas}`}
            </div>
            {getTitle()}
          </DialogTitle>
          <DialogContent
            style={{
              padding: "20px 10px",
            }}
          >
            {componente}
          </DialogContent>
          <DialogActions
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <Button
              onClick={() => handleActions(CANCELAR_VENDA_ACTION)}
              color="secondary"
              variant="contained"
              tabindex="-1"
            >
              {`VOLTAR (${CANCELAR_VENDA_ACTION})`}
            </Button>
            { tela === TELA_INCLUIR_CADASTRO ? <>
              <Button
                color="secondary"
                variant="contained"
                onClick={createCliente}
                tabindex="-1"
              >
                {`Novo cliente (${NOVO_CLIENTE_ACTION})`}
              </Button>
              <Button
                color="secondary"
                variant="contained"
                onClick={editarCliente}
                tabindex="-1"
              >
                {`Editar cliente (${EDITAR_CLIENTE_ACTION})`}
              </Button>
            </> : null}
            <Button
              onClick={() => handleActions(FECHAR_VENDA_ACTION)}
              color="secondary"
              variant="contained"
              tabindex="-1"
              disabled={avancarDesabilitadoTelaCliente}
            >
              {`AVANÇAR (${FECHAR_VENDA_ACTION})`}
            </Button>
          </DialogActions>
        </Dialog>
        <DialogoConfirmacao
          ref={refDialogoConfirmacao}
          handleConfirma={() => {
            irParaTelaInit();
          }}
          isInput
        />
        <DialogoCliente
          ref={refDialogoCliente}
          usar_biometria={usar_biometria}
          handleClose={(idUpdate) => {
            if (idUpdate > 0) {
              updateCliente(idUpdate);
            }
          }}
          openConsultorPrecos={() => {}}
        />
      </div>
    );
  }
);

export default DialogoCpfNota;
